import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, Badge, Button } from '../../components/ui/UI';
import { ArrowLeft, ShieldCheck, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import './ObservationDetailPage.css';

const Field = ({ label, value }) => (
  <div className="detail-field">
    <span className="detail-label">{label}</span>
    <span className="detail-value">{value || '—'}</span>
  </div>
);

export default function ObservationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [obs, setObs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/observations/${id}`)
      .then(({ data }) => setObs(data))
      .catch(() => navigate('/admin/observations'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return (
    <div className="fade-in">
      <div className="skeleton" style={{ height: 24, width: 200, marginBottom: 20 }} />
      <div className="skeleton" style={{ height: 300 }} />
    </div>
  );

  if (!obs) return null;

  const isSafe = obs.behaviorType === 'Safe Behavior';

  return (
    <div className="fade-in">
      <div className="detail-header">
        <Button variant="ghost" size="sm" icon={<ArrowLeft size={13} />} onClick={() => navigate(`/admin/observations/observer/${encodeURIComponent(obs.fullName)}`)}>
          Back
        </Button>
        <div className="detail-title-row">
          <h1 className="detail-title">Observation S/N {obs.sn}</h1>
          <Badge variant={isSafe ? 'safe' : 'risk'}>
            {isSafe ? <><ShieldCheck size={11} /> Safe Behavior</> : <><AlertTriangle size={11} /> At-Risk Behavior</>}
          </Badge>
        </div>
        <p className="detail-sub">
          Submitted {obs.createdAt ? format(new Date(obs.createdAt), "dd MMM yyyy 'at' HH:mm") : '—'}
        </p>
      </div>

      <div className="detail-grid">
        {/* Section 1: Observer Info */}
        <Card>
          <div className="section-label">Observer Information</div>
          <div className="fields-list">
            <Field label="S/N" value={obs.sn} />
            <Field label="Full Name" value={obs.fullName} />
            <Field label="Email Address" value={obs.email} />
            <Field label="Business Unit" value={obs.businessUnit} />
            <Field label="Department" value={obs.department} />
            <Field label="Designation" value={obs.designation} />
          </div>
        </Card>

        {/* Section 2: Observation Context */}
        <Card>
          <div className="section-label">Observation Context</div>
          <div className="fields-list">
            <Field label="Observation Date" value={obs.observationDate ? format(new Date(obs.observationDate), 'dd/MM/yyyy') : '—'} />
            <Field label="Shift" value={obs.shift} />
            <Field label="Observation Location" value={obs.observationLocation} />
          </div>
        </Card>

        {/* Section 3: Behavior Classification */}
        <Card>
          <div className="section-label">Behavior Classification</div>
          <div className="fields-list">
            <Field label="Behavior Type" value={obs.behaviorType} />
            <Field label="Behavior Category" value={obs.behaviorCategory} />
            <Field label="Antecedent / Possible Reason" value={obs.antecedent} />
          </div>
        </Card>

        {/* Section 4: Narrative & Status */}
        <Card>
          <div className="section-label">Narrative & Status</div>
          <div className="fields-list">
            <div className="detail-field detail-field--full">
              <span className="detail-label">Behavior Description</span>
              <span className="detail-value detail-value--block">{obs.behaviorDescription || '—'}</span>
            </div>
            <div className="detail-field detail-field--full">
              <span className="detail-label">Other Behaviors Observed</span>
              <span className="detail-value detail-value--block">{obs.otherBehaviorsObserved || '—'}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
